import * as React from "react";
import { Button, Spinner, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { InvoiceFields } from "../../services/API/InvoiceReports";
import { InvoiceDuplicateCheckItem } from "../../services/API/Invoices";
import { DataService } from "../../services/DataService";
import { ReadFriendlyNumber } from "../../lib/utils";
import { StyledModal } from "./StyledModal";
import Datalist, { ListColumn } from "../Datalist/Datalist";
import StyledCheckbox from "../StyledCheckbox/StyledCheckbox";
import { normalizeDuplicateAmount } from "../../lib/InvoiceDuplicateValidation";
import "./InvoiceReportDuplicatesModal.scss";

interface InvoiceDuplicateGroup {
  id: number;
  groupId: number;
  customer: string;
  customerOrgId: string;
  contractId: string;
  invoiceNumber: string;
  billingDate: string;
  amount: number;
  rowCount: number;
}

interface Props {
  invoices: InvoiceFields[];
  reportingArticles: boolean;
  mode?: "submit" | "import";
  onDismiss(): void;
  onVerified(): void;
  onRemoveGroups(groupIds: number[]): void;
}

const getInvoiceDuplicateGroups = (
  invoices: InvoiceFields[],
  reportingArticles: boolean,
): InvoiceDuplicateGroup[] => {
  const groupedInvoices = reportingArticles
    ? Object.values(
        invoices.reduce((acc: Record<number, InvoiceFields[]>, invoice) => {
          if (!acc[invoice.groupId]) {
            acc[invoice.groupId] = [];
          }

          acc[invoice.groupId].push(invoice);
          return acc;
        }, {}),
      )
    : invoices.map((invoice) => [invoice]);

  return groupedInvoices.map((group) => {
    const first = group[0];
    return {
      id: first.groupId,
      groupId: first.groupId,
      customer: first.customer.value,
      customerOrgId: first.customerOrgId.value,
      contractId: first.contractId.value,
      invoiceNumber: first.invoiceNumber.value,
      billingDate: first.billingDate.value,
      amount: group.reduce(
        (sum, invoice) =>
          sum + normalizeDuplicateAmount(invoice.rowSumExclMoms.value),
        0,
      ),
      rowCount: group.length,
    };
  });
};

export function InvoiceReportDuplicatesModal(props: Props) {
  const { invoices, onVerified, reportingArticles } = props;
  const { t } = useTranslation();
  const mode = props.mode ?? "submit";
  const [loading, setLoading] = React.useState(true);
  const [duplicates, setDuplicates] = React.useState<InvoiceDuplicateGroup[]>(
    [],
  );
  const [selectedGroupIds, setSelectedGroupIds] = React.useState<number[]>([]);
  const [errorMessage, setErrorMessage] = React.useState("");
  const selectedGroupIdsRef = React.useRef(selectedGroupIds);
  selectedGroupIdsRef.current = selectedGroupIds;

  const [columns, setColumns] = React.useState<
    ListColumn<InvoiceDuplicateGroup>[]
  >([
    {
      key: "select",
      fieldName: "groupId",
      name: t("invoiceReport.duplicates.select") as string,
      minWidth: 55,
      maxWidth: 55,
      isActive: true,
      sorting: false,
      onRender: (item) => (
        <StyledCheckbox
          isChecked={selectedGroupIdsRef.current.includes(item.groupId)}
          onChange={() => toggleSelected(item.groupId)}
          label=""
        />
      ),
    },
    {
      key: "customer",
      fieldName: "customer",
      name: t("invoiceReport.table.headers.customer") as string,
      minWidth: 160,
      isActive: true,
      sorting: true,
    },
    {
      key: "customerOrgId",
      fieldName: "customerOrgId",
      name: t("invoiceReport.table.headers.customerOrgId") as string,
      minWidth: 130,
      isActive: true,
      sorting: true,
    },
    {
      key: "contractId",
      fieldName: "contractId",
      name: t("invoiceReport.table.headers.contractId") as string,
      minWidth: 130,
      isActive: true,
      sorting: true,
    },
    {
      key: "invoiceNumber",
      fieldName: "invoiceNumber",
      name: t("invoiceReport.table.headers.invoiceNumber") as string,
      minWidth: 130,
      isActive: true,
      sorting: true,
    },
    {
      key: "billingDate",
      fieldName: "billingDate",
      name: t("invoiceReport.table.headers.date") as string,
      minWidth: 120,
      isActive: true,
      sorting: true,
    },
    {
      key: "amount",
      fieldName: "amount",
      name: t("invoiceReport.table.headers.sum") as string,
      minWidth: 130,
      isActive: true,
      sorting: true,
      onRender: (item) => ReadFriendlyNumber(item.amount),
    },
    {
      key: "rowCount",
      fieldName: "rowCount",
      name: t("invoiceReport.duplicates.rows") as string,
      minWidth: 80,
      isActive: true,
      sorting: true,
    },
    {
      key: "reason",
      fieldName: "reason",
      name: t("invoiceReport.duplicates.reason") as string,
      minWidth: 280,
      isActive: true,
      sorting: false,
      onRender: () => t("invoiceReport.duplicates.defaultReason") as string,
    },
  ]);

  React.useEffect(() => {
    let cancelled = false;

    const loadDuplicates = async () => {
      try {
        setLoading(true);
        setErrorMessage("");

        const invoiceGroups = getInvoiceDuplicateGroups(
          invoices,
          reportingArticles,
        );

        const duplicateCheckItems: InvoiceDuplicateCheckItem[] =
          invoiceGroups.map((invoice) => ({
            groupId: invoice.groupId,
            contractId: invoice.contractId,
            invoiceNumber: invoice.invoiceNumber,
            billingDate: invoice.billingDate,
            amount: invoice.amount,
          }));

        const duplicateResult = await DataService.Invoices.CheckDuplicates(
          duplicateCheckItems,
        );

        if (cancelled) return;

        const duplicateGroupIds = new Set(
          duplicateResult.duplicates.map((duplicate) => duplicate.groupId),
        );
        const duplicateGroups = invoiceGroups.filter((invoice) =>
          duplicateGroupIds.has(invoice.groupId),
        );

        if (duplicateGroups.length === 0) {
          onVerified();
          return;
        }

        setDuplicates(duplicateGroups);
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(t("invoiceReport.duplicates.fetchError") as string);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDuplicates();

    return () => {
      cancelled = true;
    };
  }, [invoices, onVerified, reportingArticles, t]);

  const toggleSelected = (groupId: number) => {
    setSelectedGroupIds((current) =>
      current.includes(groupId)
        ? current.filter((id) => id !== groupId)
        : current.concat(groupId),
    );
  };

  const removeSelected = () => {
    if (selectedGroupIds.length === 0) return;
    props.onRemoveGroups(selectedGroupIds);
  };

  return (
    <StyledModal
      className="invoice-report-duplicates-modal"
      headerStyle={{ backgroundColor: "#173c53", color: "white" }}
      onDismiss={props.onDismiss}
      title={t("invoiceReport.duplicates.title").toString()}
      body={
        <Stack gap={3}>
          {loading && (
            <Stack direction="horizontal" gap={2}>
              <Spinner animation="border" size="sm" />
              <span>{t("invoiceReport.duplicates.loading")}</span>
            </Stack>
          )}

          {!loading && errorMessage && (
            <div className="error-message">
              <span>{t("general.errorMsg")}:</span> {errorMessage}
            </div>
          )}

          {!loading && !errorMessage && (
            <>
              <div>
                <strong>
                  {t("invoiceReport.duplicates.count", {
                    count: duplicates.length,
                  })}
                </strong>{" "}
                {t(`invoiceReport.duplicates.${mode}.description`)}
              </div>
              <div className="invoice-report-duplicates-table">
                <Datalist
                  items={duplicates}
                  columns={columns}
                  setColumns={setColumns}
                  noItemsText={t("invoiceReport.duplicates.noRows") as string}
                  uniqueKey="groupId"
                  disableHover
                />
              </div>
            </>
          )}
        </Stack>
      }
      footer={
        <Stack direction="horizontal" gap={2} style={{ flexWrap: "wrap" }}>
          <Button className="button button-cancel" onClick={props.onDismiss}>
            {t(`invoiceReport.duplicates.${mode}.dismiss`)}
          </Button>
          <Button
            className="button button-danger"
            disabled={loading || selectedGroupIds.length === 0}
            onClick={removeSelected}
          >
            {t(`invoiceReport.duplicates.${mode}.removeSelected`)}
          </Button>
        </Stack>
      }
    />
  );
}
